// src/components/OrderDetailsModalFull.jsx
import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  User,
  Phone,
  MapPin,
  Wallet,
  CalendarDays,
  Clock,
  FileText,
  Link as LinkIcon,
  Tag,
  Truck,
  Utensils,
  Store,
  Info,
} from "lucide-react";
import axios from "axios";


const formatCurrency = (v) => {
  // you can replace "₹" with env currency sign if needed
  if (v === undefined || v === null || isNaN(v)) return localStorage.getItem('currency')+"0.00";
  return `${localStorage.getItem('currency')}${Number(v).toFixed(2)}`;
};

const SafeParseJSON = (s) => {
  try {
    return typeof s === "string" ? JSON.parse(s) : s || {};
  } catch (e) {
    return {};
  }
};

const Badge = ({ children, color = "gray" }) => (
  <span
    className={`inline-block text-xs px-2 py-0.5 rounded-full bg-${color}-50 text-${color}-700`}
  >
    {children}
  </span>
);

// small defensive helper to read nested arrays safely
const safeArr = (a) => (Array.isArray(a) ? a : []);

export default function OrderDetailsModalFull({ orderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    setOrder(null);
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function fetchOrder() {
    setLoading(true);
    try {
      // adapt URL if your API differs. Using previously used base endpoint
      const res = await axios.get(
        `${window.BASE_API_URL}orderdetails/${orderId}`
      );
      if (res?.data?.success) {
        setOrder(res.data.data);
      } else {
        console.error("unexpected API response", res);
        setOrder(res?.data?.data || null);
      }
    } catch (err) {
      console.error("Failed to fetch order details", err);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }

  // compute amount breakdown in the same sequence as blade
  const amountBreakdown = useMemo(() => {
    if (!order) return null;

    // amount_data might be stringified JSON or already object
    const ad = SafeParseJSON(order.amount_data || {});
    const breakdown = [];
    let cart_total = Number(ad.subtotal || 0);

    // SUBTOTAL
    breakdown.push({ key: "Subtotal", value: Number(ad.subtotal || 0) });

    // PROMO vs SPECIAL
    if (ad.is_promo === 1) {
      // promocode applied
      if (ad.promocode_value !== undefined && ad.promocode_value !== "") {
        if (ad.promocode_type === "P") {
          // percent on promocode_onamount or cart_total
          const base = Number(ad.promocode_onamount ?? cart_total);
          const amt = (base * Number(ad.promocode_value || 0)) / 100;
          if (amt > 0) {
            breakdown.push({
              key: `Promo (${ad.promocode_name || "Code"}) - ${ad.promocode_value}%`,
              value: -amt,
              meta: `of ${formatCurrency(base)}`,
            });
            cart_total -= amt;
          }
        } else {
          // fixed
          let amt = Number(ad.promocode_value || 0);
          if (cart_total < amt) amt = cart_total;
          breakdown.push({
            key: `Promo (${ad.promocode_name || "Code"})`,
            value: -amt,
          });
          cart_total -= amt;
        }
      }
    } else {
      // special discount block (only when is_promo != 1)
     if (ad.special_value !== undefined && ad.special_value !== "") {
  const label = ad.special_label || "Special Discount";

  if (ad.special_type === "P") {
    const base = Number(ad.special_onamount ?? cart_total);
    const amt = (base * Number(ad.special_value || 0)) / 100;

    if (amt > 0) {
      breakdown.push({
        key: `${label} - ${ad.special_value}%`,
        value: -amt,
        meta: `of ${formatCurrency(base)}`,
      });
      cart_total -= amt;
    }
  } else {
    // fixed
    let amt = Number(ad.special_value || 0);
    if (cart_total < amt) amt = cart_total;

    breakdown.push({
      key: label,
      value: -amt,
    });

    cart_total -= amt;
  }
}
    }

    // WELCOME (first order)
    if (ad.welcome_value && Number(ad.welcome_value) > 0) {
      if (ad.welcome_type === "1") {
        const amt = (cart_total * Number(ad.welcome_value || 0)) / 100;
        breakdown.push({
          key: `First Order Discount - ${ad.welcome_value}%`,
          value: -amt,
        });
        cart_total -= amt;
      } else {
        const amt = Number(ad.welcome_value || 0);
        breakdown.push({ key: "First Order Discount", value: -amt });
        cart_total -= amt;
      }
    }

    // At this point blade stores after_discount = cart_total; and total = cart_total
    // taxes: tax_type 0 => percent-based on cart_total; tax_type 1 => precomputed amounts
    const tax_type = ad.tax_type === "" || ad.tax_type === undefined ? 0 : Number(ad.tax_type);

    if (tax_type === 0) {
      // tax_value is array
      safeArr(ad.tax_value).forEach((tv, i) => {
        const pct = Number(tv || 0);
        if (pct === 0) return;
        const label = (ad.tax_label && ad.tax_label[i]) || `Tax ${i + 1}`;
        const taxAmt = (cart_total * pct) / 100;
        breakdown.push({
          key: `${label} (${pct}%)`,
          value: Number(taxAmt),
          meta: `of ${formatCurrency(cart_total)}`,
        });
      });
    } else {
      // tax_type==1: taxes precomputed in ad.tax_amount etc.
      if (ad.tax_amount) {
        breakdown.push({
          key: `Tax (${ad.tax_label || "Tax"})`,
          value: Number(ad.tax_amount),
          meta: `(included)`,
        });
      }
    }

    // additionals (loop)
    safeArr(ad.additional_value).forEach((val, i) => {
      const t = ad.additional_type?.[i] ?? "F";
      const label = ad.additional_label?.[i] ?? `Charge ${i + 1}`;
      if (t === "P") {
        const amt = (cart_total * Number(val || 0)) / 100;
        breakdown.push({
          key: `${label} (${val}%)`,
          value: Number(amt),
          meta: `of ${formatCurrency(cart_total)}`,
        });
      } else {
        const amt = Number(val || 0);
        breakdown.push({ key: label, value: Number(amt) });
      }
    });

    // tips
    if (ad.tip_value && ad.tip_value !== "") {
      if (ad.tip_type === "P") {
        const amt = (cart_total * Number(ad.tip_value || 0)) / 100;
        breakdown.push({ key: `Tips (${ad.tip_value}%)`, value: Number(amt) });
      } else {
        const amt = Number(ad.tip_value || 0);
        breakdown.push({ key: "Tips", value: Number(amt) });
      }
    }

    // delivery charge
    if (ad.delivery_charge && Number(ad.delivery_charge) > 0) {
      breakdown.push({
        key: "Delivery Charge",
        value: Number(ad.delivery_charge),
      });
    }

    // Rewards (redeemed)
    if (order.is_rewards === 1 && Number(order.rewards_amount || 0) > 0) {
      breakdown.push({
        key: `Rewards Points Redeemed (${order.rewards_point || ""})`,
        value: -Number(order.rewards_amount || 0),
      });
    }

    // After all incremental items above, Blade computes $total from these elements.
    // However we didn't maintain a running total adding taxes/additionals/tips/delivery individually to `cart_total`.
    // To mimic Blade final total reliably, we will compute finalTotal either from order.amount (server authoritative)
    // or by summing list.
    let computedTotal = 0;
    // start from subtotal
    computedTotal += Number(ad.subtotal || 0);
    // add each breakdown value (promo/special are negative entries)
    for (const b of breakdown.slice(1)) {
      // note: we added subtotal as first element; slice(1) iterates others
      computedTotal += Number(b.value || 0);
    }

    // NOTE: blade sometimes uses separate logic for tax_type==0 where taxes were added to $total variable,
    // we attempted to add those when building 'breakdown' above. computedTotal should match final amount if data is consistent.
    // Use order.amount as authoritative final total (as Blade displays $cart->amount).
    const finalTotal = Number(order.amount ?? computedTotal);

    // Voucher / paid amount rendering (Blade shows apply voucher & pay amount)
    const voucherAmt = Number(order.voucher_total_amount || 0);
    const paidAmount = Number(order.paid_amount || 0);

    // reward earn messages
    const earned = ad.earn_rewards ? Number(ad.earn_rewards) : null;
    const firstEarn = ad.first_earn_rewards ? Number(ad.first_earn_rewards) : null;

    return {
      ad,
      breakdown,
      computedTotal,
      finalTotal,
      voucherAmt,
      paidAmount,
      earned,
      firstEarn,
    };
  }, [order]);

  // Render helpers for items (type 1,2,3)
  const ItemRegular = ({ item, ix }) => {
    const menucat = item.menucategory;
    const mcNames = menucat?.name || [];
    return (
      <div key={ix} className="border rounded-xl mb-3 bg-white overflow-hidden">
        <details className="group">
          <summary className="px-3 py-3 flex justify-between items-center cursor-pointer">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{item.quantity} × {item.title}</span>
                {item.cattype === 1 && <Badge color="green">First Order Offer</Badge>}
                {item.cattype === 2 && <Badge color="blue">Min Order Item</Badge>}
              </div>
            </div>
            <div className="font-semibold text-green-600">
              {formatCurrency(Number(item.total || 0) * Number(item.quantity || 1))}
            </div>
          </summary>

          <div className="p-3 border-t bg-gray-50 text-sm">
            <div className="flex justify-between">
              <span>Base</span>
              <span>{formatCurrency(item.price || 0)}</span>
            </div>

            {Array.isArray(mcNames) && mcNames.length > 0 && (
              <>
                {item.quantity > 1 && (
                  <div className="text-xs font-semibold mt-2 text-rose-600">
                    Individual Toppings per Item
                  </div>
                )}
                {mcNames.map((cat, idx) => {
                  const opt = SafeParseJSON(item.menucategory.data[idx] || "{}");
                  return (
                    <div key={idx} className="mt-2 p-2 rounded bg-white">
                      <div className="font-medium">{cat}</div>
                      {Array.isArray(opt.name) &&
                        opt.name.map((name, j) => (
                          <div key={j} className="flex justify-between text-sm pl-4 mt-1">
                            <div>
                              {opt.quantity?.[j] > 1 ? `${opt.quantity[j]} × ` : ""}
                              {name}
                            </div>
                            <div>{formatCurrency(opt.price?.[j] ?? 0)}</div>
                          </div>
                        ))}
                    </div>
                  );
                })}
              </>
            )}

            {item.spice_level && (
              <div className="mt-2 bg-amber-50 text-amber-700 px-2 py-1 rounded text-xs">🌶 Spice: {item.spice_level}</div>
            )}

            {item.special_instruction && (
              <div className="mt-2 bg-orange-50 text-orange-700 px-2 py-1 rounded italic text-xs">📝 {item.special_instruction}</div>
            )}
          </div>
        </details>
      </div>
    );
  };

  const ItemHalf = ({ item, ix }) => {
    return (
      <div key={ix} className="border rounded-xl mb-3 bg-white overflow-hidden">
        <details className="group">
          <summary className="px-3 py-3 flex justify-between items-center cursor-pointer">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{item.quantity} × {item.title} (Half-n-Half)</span>
              </div>
            </div>
            <div className="font-semibold text-green-600">
              {formatCurrency(Number(item.total || 0) * Number(item.quantity || 1))}
            </div>
          </summary>

          <div className="p-3 border-t bg-gray-50 text-sm space-y-2">
            {Array.isArray(item.halfnhalf_title) && item.halfnhalf_title.map((h, idx) => (
              <div key={idx} className="p-2 rounded bg-white">
                <div className="font-semibold">{h} ({idx === 0 ? 'First Half' : 'Second Half'})</div>
                <div className="flex justify-between text-sm mt-1">
                  <span>Base Price</span>
                  <span>{formatCurrency(item.halfnhalf_price?.[idx] ?? 0)}</span>
                </div>

                {item.data?.[idx] && item.data[idx].name && item.data[idx].name.map((cat, ci) => {
                  const opt = SafeParseJSON(item.data[idx].menucategory.data[ci] || "{}");
                  return (
                    <div key={ci} className="mt-2">
                      <div className="font-medium">{cat}</div>
                      {opt.name?.map((nm, j) => (
                        <div key={j} className="flex justify-between pl-4 text-sm mt-1">
                          <div>{opt.quantity?.[j] > 1 ? `${opt.quantity[j]} × ` : ""}{nm}</div>
                          <div>{formatCurrency(opt.price?.[j] ?? 0)}</div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}

            {item.special_instruction && (
              <div className="mt-2 bg-orange-50 text-orange-700 px-2 py-1 rounded italic text-xs">📝 {item.special_instruction}</div>
            )}
          </div>
        </details>
      </div>
    );
  };

 const ItemCombo = ({ item, ix }) => {
  return (
    <div key={ix} className="border rounded-xl mb-3 bg-white overflow-hidden">
      <details className="group">
        <summary className="px-3 py-3 flex justify-between items-center cursor-pointer">
          <div className="font-semibold">
            {item.quantity} × {item.title} (Combo)
          </div>
          <div className="font-semibold text-green-600">
            {formatCurrency(Number(item.total || 0) * Number(item.quantity || 1))}
          </div>
        </summary>

        <div className="p-3 border-t bg-gray-50 text-sm space-y-3">

          {item.combocategory?.item?.map((it, ci) => (
            <div key={ci} className="p-2 rounded bg-white">

              <div className="font-semibold">{it.category_name?.[0]}</div>

              {it.combo_data?.map((combo, idx) => {
                const qty = Number(combo.menu_qty || 1);

                // 🔥 CALCULATE MODIFIER TOTAL
                let addonAmt = 0;

                if (Array.isArray(it.data) && it.data[idx]) {
                  const dataNode = it.data[idx];

                  dataNode.menucategory?.data?.forEach((dd, i) => {
                    const opt = SafeParseJSON(dd || "{}");

                    opt.price?.forEach((p, j) => {
                      addonAmt += (Number(p || 0) * qty);
                    });
                  });
                }

                const baseTotal = Number(combo.price || 0) * qty;
                const total = baseTotal + addonAmt;

                return (
                  <div key={idx} className="mt-2">

                    {/* HEADER */}
                    <div className="flex justify-between font-medium">
                      <div>{qty} × {combo.title}</div>
                      <div>
                        {combo.with_price ? formatCurrency(total) : "-"}
                      </div>
                    </div>

                    {/* BASE */}
                    <div className="flex justify-between text-sm pl-4 mt-1">
                      <div>Base</div>
                      <div>{formatCurrency(baseTotal)}</div>
                    </div>

                    {/* INDIVIDUAL TOPPING LABEL */}
                    {qty > 1 && (
                      <div className="text-xs text-rose-600 pl-4 mt-1 font-semibold">
                        Individual Toppings per Item
                      </div>
                    )}

                    {/* MODIFIERS */}
                    {it.data?.[idx]?.name?.map((cat, ci2) => {
                      const opt = SafeParseJSON(
                        it.data[idx].menucategory.data[ci2] || "{}"
                      );

                      return (
                        <div key={ci2} className="pl-4 mt-2">

                          <div className="font-medium">{cat}</div>

                          {opt.name?.map((nm, j) => (
                            <div key={j} className="flex justify-between pl-4 text-sm mt-1">
                              <div>
                                {opt.quantity?.[j] > 1 ? `${opt.quantity[j]} × ` : ""}
                                {nm}
                              </div>
                              <div>
                                {formatCurrency((opt.price?.[j] || 0) * qty)}
                              </div>
                            </div>
                          ))}

                        </div>
                      );
                    })}

                    {/* SPICE */}
                    {it.combo_spice_level?.[idx] && (
                      <div className="text-xs text-amber-600 mt-1 pl-4">
                        🌶 Spice: {it.combo_spice_level[idx]}
                      </div>
                    )}

                    {/* NOTE */}
                    {it.combo_special_instruction?.[idx] && (
                      <div className="text-xs italic text-orange-600 mt-1 pl-4">
                        📝 {it.combo_special_instruction[idx]}
                      </div>
                    )}

                  </div>
                );
              })}

            </div>
          ))}

          {item.special_instruction && (
            <div className="mt-2 bg-orange-50 text-orange-700 px-2 py-1 rounded italic text-xs">
              📝 {item.special_instruction}
            </div>
          )}
        </div>
      </details>
    </div>
  );
};

  if (!orderId) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">Order #{order?.order_no ?? orderId}</h2>
              {order?.is_advance === 1 && <Badge color="green">Advance Order</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden">
            {/* Left: Items */}
            <div className="flex-1 border-r border-gray-100 p-5 overflow-y-auto">
              {loading ? (
                <div className="text-center text-gray-400">Loading order...</div>
              ) : (
                <>
                  <h3 className="text-lg font-bold mb-4">Ordered Items</h3>

                  {order?.items?.length ? (
                    order.items.map((it, idx) => {
                      if (it.type === 1) return <ItemRegular key={idx} item={it} ix={idx} />;
                      if (it.type === 2) return <ItemCombo key={idx} item={it} ix={idx} />;
                      if (it.type === 3) return <ItemHalf key={idx} item={it} ix={idx} />;
                      // default:
                      return <ItemRegular key={idx} item={it} ix={idx} />;
                    })
                  ) : (
                    <div className="text-sm text-gray-500">No items found.</div>
                  )}
                </>
              )}
            </div>

            {/* Right: Order Info & Amount */}
            <div className="w-full lg:w-[38%] p-5 overflow-y-auto">
              {loading ? (
                <div className="text-center text-gray-400">Fetching details...</div>
              ) : (
                <>
                  <h3 className="text-lg font-bold mb-3">Order Information</h3>

                  <div className="space-y-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-blue-500" />
                      <div>
                        <div className="text-xs text-gray-500">Order Type</div>
                        <div className="font-medium">{order?.delivery_type_label ?? order?.delivery_type}</div>
                      </div>
                    </div>

                    {order?.delivery_type === "DI" && order?.table_name && (
                      <div className="flex items-center gap-2">
                        <Utensils size={14} className="text-indigo-500" />
                        <div>
                          <div className="text-xs text-gray-500">Table</div>
                          <div className="font-medium">{order.table_name}</div>
                        </div>
                      </div>
                    )}

                    {order?.is_guest === 1 && (
                      <div className="text-xs text-orange-600 font-semibold">Guest User</div>
                    )}

                    <div className="flex items-center gap-2">
                      <User size={14} className="text-violet-500" />
                      <div>
                        <div className="text-xs text-gray-500">Customer</div>
                        <div className="font-medium">{order?.name}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-sky-500" />
                      <div>
                        <div className="text-xs text-gray-500">Phone</div>
                        <div className="font-medium">{order?.phone_no}</div>
                      </div>
                    </div>

                    {order?.full_address && (
                      <div className="flex items-start gap-2">
                        <MapPin size={14} className="text-red-500 mt-0.5" />
                        <div>
                          <div className="text-xs text-gray-500">Address</div>
                          <div className="text-sm">{order.full_address}</div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <Wallet size={14} className="text-emerald-500" />
                      <div>
                        <div className="text-xs text-gray-500">Payment</div>
                        <div className="font-medium">
                          {order?.payment_method ?? (order?.payment_type === 0 ? "Cash" : "Card")}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <CalendarDays size={14} className="text-amber-500" />
                      <div>
                        <div className="text-xs text-gray-500">Order For</div>
                        <div className="font-medium">{order?.order_date} {order?.order_time}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-pink-500" />
                      <div>
                        <div className="text-xs text-gray-500">Created</div>
                        <div className="font-medium">
                          {order?.created_at ? new Date(order.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          }) : "-"}
                        </div>
                      </div>
                    </div>

                    {order?.new_order_time && (
                      <div className="text-sm text-gray-700">
                        ⏰ Updated ready time: <span className="font-medium">{order.new_order_time}</span>
                      </div>
                    )}

                    {order?.external_delivery_id?.startsWith?.("del") && (
                      <div className="flex items-center gap-2 text-sm text-green-700">
                        <LinkIcon size={14} />
                        <a
                          className="underline"
                          target="_blank"
                          rel="noreferrer"
                          href={`/track-order/${order.id}`}
                        >
                          Track Order
                        </a>
                      </div>
                    )}

                    {order?.special_instruction && (
                      <div className="mt-2 bg-orange-50 text-orange-700 px-2 py-1 rounded italic text-sm">
                        📝 {order.special_instruction}
                      </div>
                    )}
                  </div>

                  {/* Amount Summary */}
                  <div className="mt-6 border-t pt-3">
                    <h4 className="font-semibold text-gray-800 mb-3">Amount Summary</h4>

                    <div className="text-sm text-gray-700 space-y-2">
                      {/* Render breakdown entries */}
                      {(amountBreakdown?.breakdown || []).map((b, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <div>
                            <div className="text-xs text-gray-500">{b.key}</div>
                            {b.meta && <div className="text-[12px] text-gray-400">{b.meta}</div>}
                          </div>
                          <div className={`${b.value < 0 ? "text-red-600" : "text-gray-900"} font-medium`}>
                            {b.value < 0 ? "-" : ""}{formatCurrency(Math.abs(b.value || 0))}
                          </div>
                        </div>
                      ))}

                      {/* Voucher & Pay amount (blade shows voucher block if is_voucher) */}
                      {order?.is_voucher == 1 && Number(order?.voucher_total_amount || 0) > 0 && (
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-gray-500">Applied Voucher</div>
                          <div className="text-red-600 font-medium">-{formatCurrency(Number(order.voucher_total_amount || 0))}</div>
                        </div>
                      )}

                      {/* Final Total */}
                      <div className="flex justify-between items-center border-t pt-2 mt-2">
                        <div className="font-semibold">Total</div>
                        <div className="font-semibold text-green-600">{formatCurrency(amountBreakdown?.finalTotal ?? order?.amount ?? 0)}</div>
                      </div>

                      {/* Paid / Pay amount */}
                      {order?.is_voucher === 1 && (
                        <div className="flex justify-between items-center">
                          <div className="text-xs text-gray-500">Pay Amount</div>
                          <div className="font-medium">{formatCurrency(Number(order.paid_amount || 0))}</div>
                        </div>
                      )}

                      {/* Rewards info */}
                      {amountBreakdown?.earned && (
                        <div className="text-center text-green-600 text-sm mt-2">
                          User earned {amountBreakdown.earned} rewards points on this order.
                        </div>
                      )}
                      {amountBreakdown?.firstEarn && (
                        <div className="text-center text-green-600 text-sm mt-1">
                          User earned {amountBreakdown.firstEarn} rewards points on first order.
                        </div>
                      )}
                    </div>
                  </div>

                </>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
